import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'onboarding:draft';
const SCHEMA_VERSION = 1;
const MAX_DRAFT_AGE_DAYS = 7;

export const STEPS = [
    'intro',
    'services',
    'businessName',
    'website',
    'contact',
    'customer',
    'account',
];

const emptyDraft = () => ({
    version: SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    step: STEPS[0],
    completedSteps: [],
    voucherCode: null,
    data: {
        intro: {},
        services: {selected: [], custom: []},
        businessName: {},
        website: {},
        contact: {},
        customer: {},
    },
});

export const loadDraft = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return emptyDraft();
        }

        const draft = JSON.parse(raw);

        if (draft?.version !== SCHEMA_VERSION || !STEPS.includes(draft?.step)) {
            return emptyDraft();
        }

        // Drafts hold pre-auth contact details; don't keep them around forever
        const age = Date.now() - new Date(draft.updatedAt ?? 0).getTime();
        if (!draft.updatedAt || age > MAX_DRAFT_AGE_DAYS * 24 * 60 * 60 * 1000) {
            return emptyDraft();
        }

        return {...emptyDraft(), ...draft, data: {...emptyDraft().data, ...draft.data}};
    } catch {
        return emptyDraft();
    }
};

export const clearDraft = () => {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore - worst case a stale draft remains
    }
};

// The wizard's step-1 selection: predefined services picked plus any custom
// ones, resolved to {value, label, group} options for the job's service picker.
export const selectedServiceOptions = (servicesData, predefined) => {
    const {selected = [], custom = []} = servicesData ?? {};

    const options = [];

    (predefined ?? []).forEach(category => {
        category.services.forEach(service => {
            if (selected.includes(service.type)) {
                options.push({value: service.type, label: service.title, group: category.category});
            }
        });
    });

    custom.forEach(service => {
        if (selected.includes(service.id)) {
            options.push({value: service.id, label: service.title, group: service.category});
        }
    });

    return options;
};

export const buildRegisterPayload = (draft) => {
    const {services, businessName, website, contact, customer} = draft.data;

    const selected = services?.selected ?? [];
    const custom = (services?.custom ?? []).filter(s => selected.includes(s.id));
    const customIds = custom.map(s => s.id);
    const predefinedTypes = selected.filter(type => !customIds.includes(type));

    const payload = {
        version: SCHEMA_VERSION,
        services: [
            ...predefinedTypes.map(type => ({type})),
            ...custom.map(s => ({type: 'CUSTOM', id: s.id, category: s.category, title: s.title})),
        ],
        businessName: businessName?.name || null,
        website: null,
        contact: null,
        customer: null,
    };

    if (website?.choice === 'getsoapy' && website.subdomain) {
        payload.website = {choice: 'getsoapy', subdomain: website.subdomain};
    } else if (website?.choice === 'external' && website.domain) {
        payload.website = {choice: 'external', url: website.domain};
    } else if (website?.choice === 'later') {
        payload.website = {choice: 'later'};
    }

    if (!contact?.skipped && (contact?.phoneNumber || contact?.address)) {
        payload.contact = {
            phoneNumber: contact.phoneNumber ?? null,
            address: contact.address ?? null,
        };
    }

    if (!customer?.skipped && customer?.name) {
        // Only keep job services that are still selected in step 1
        const jobServices = (customer.job?.services ?? []).filter(s => selected.includes(s));

        payload.customer = {
            customerId: customer.customerId,
            name: customer.name,
            type: customer.type ?? 'Unknown',
            businessName: customer.businessName ?? null,
            email: customer.email ?? null,
            phoneNumber: customer.phoneNumber ?? null,
            address: customer.address ?? null,
            job: jobServices.length > 0
                ? {
                    jobId: customer.job.jobId,
                    services: jobServices,
                    frequency: customer.job.frequency ?? null,
                    cost: customer.job.cost ?? null,
                    duration: customer.job.duration ?? null,
                    notes: customer.job.notes ?? null,
                }
                : null,
        };
    }

    return payload;
};

const OnboardingContext = createContext(null);

export const useOnboarding = () => useContext(OnboardingContext);

export const OnboardingProvider = ({children}) => {
    const [draft, setDraft] = useState(loadDraft);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
        } catch {
            // Storage full/unavailable - the wizard still works, just won't resume
        }
    }, [draft]);

    const value = useMemo(() => {
        const stepIndex = STEPS.indexOf(draft.step);

        const goTo = (step) => setDraft(prev => ({
            ...prev,
            step,
            updatedAt: new Date().toISOString(),
        }));

        return {
            draft,
            steps: STEPS,
            step: draft.step,
            stepIndex,
            goTo,
            next: () => setDraft(prev => {
                const index = STEPS.indexOf(prev.step);
                return {
                    ...prev,
                    step: STEPS[Math.min(index + 1, STEPS.length - 1)],
                    completedSteps: prev.completedSteps.includes(prev.step)
                        ? prev.completedSteps
                        : [...prev.completedSteps, prev.step],
                    updatedAt: new Date().toISOString(),
                };
            }),
            back: () => setDraft(prev => {
                const index = STEPS.indexOf(prev.step);
                return {
                    ...prev,
                    step: STEPS[Math.max(index - 1, 0)],
                    updatedAt: new Date().toISOString(),
                };
            }),
            updateStepData: (stepId, data) => setDraft(prev => ({
                ...prev,
                data: {...prev.data, [stepId]: data},
                updatedAt: new Date().toISOString(),
            })),
            markSkipped: (stepId) => setDraft(prev => ({
                ...prev,
                data: {...prev.data, [stepId]: {...prev.data[stepId], skipped: true}},
                updatedAt: new Date().toISOString(),
            })),
            setVoucherCode: (voucherCode) => setDraft(prev => (
                prev.voucherCode === voucherCode ? prev : {...prev, voucherCode}
            )),
            voucherCode: draft.voucherCode,
            buildRegisterPayload: () => buildRegisterPayload(draft),
            clearDraft,
        };
    }, [draft]);

    return <OnboardingContext.Provider value={value}>
        {children}
    </OnboardingContext.Provider>;
};

export default OnboardingProvider;
